import React, { useEffect, useState } from "react";
import { Row, Col, Container, Button, Spinner } from "reactstrap";
import AssetOverview from "./AssetOverview";
import Charts from "./Charts";
import img from "../../../assets/images/logo.png";
import { RiArrowLeftLine, RiFileDownloadLine, RiFileChartLine } from "react-icons/ri";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import RecommendationSumary from "./RecommendationSumary";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import ThreatList from "./ThreatList";
import { trackDownload } from "../../../services/activityTracker";

const ORANGE_COLORS = {
    primary: '#FF5B00',
    dark:    '#e85a28',
};

function AgentReport() {
    const { agent }   = useParams();
    const query       = new URLSearchParams(useLocation().search);
    const days        = query.get("days");
    const navigate    = useNavigate();

    const [exportLoading, setExportLoading] = useState(false);
    const [pdfLoading, setPdfLoading]       = useState(false);
    const [reportData, setReportData]       = useState(null);
    const [reportLoading, setReportLoading] = useState(true);

    // ── PDF download (unchanged) ─────────────────────────────────────────────
    const downloadPDF = async () => {
        setPdfLoading(true);
        try {
            const input     = document.getElementById("AgentReport");
            const canvas    = await html2canvas(input, { scale: 3 });
            const imgData   = canvas.toDataURL("image/png", 1.0);
            const pdf       = new jsPDF("p", "mm", "a4");
            const pageWidth = pdf.internal.pageSize.getWidth();
            const margin    = 15;
            const pdfWidth  = pageWidth - margin * 2;
            const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
            pdf.addImage(imgData, "PNG", margin, margin, pdfWidth, pdfHeight);
            pdf.save("Agent Report.pdf");
            trackDownload("Agent Report PDF", { agent, days });
        } finally {
            setPdfLoading(false);
        }
    };

    // ── Event log CSV export — calls the dedicated backend endpoint ──────────
    const downloadEventLog = async () => {
        setExportLoading(true);
        try {
            const authUser = JSON.parse(sessionStorage.getItem("authUser"));
            const headers  = {
                ...(authUser?.token && { Authorization: `Bearer ${authUser.token}` }),
            };

            // Build query params — agent and days passed explicitly
            const params = new URLSearchParams();
            params.set("agent", agent);                       // exact agent_name
            if (days && days !== "other") params.set("days", days);

            const url      = `${process.env.REACT_APP_API_BASE_URL}/threats/export?${params}`;
            const response = await fetch(url, { headers });

            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error(err.message || `Server error ${response.status}`);
            }

            // Read total row count from response header for user feedback
            const total = response.headers.get("X-Total-Rows");
            console.log(`📥 Downloading ${total} rows for agent: ${agent}`);

            // Stream the blob and trigger download
            const blob     = await response.blob();
            const filename = response.headers
                .get("Content-Disposition")
                ?.match(/filename="(.+)"/)?.[1]
                ?? `event-log_${agent}_${new Date().toISOString().slice(0,10)}.csv`;

            const link      = document.createElement("a");
            link.href       = URL.createObjectURL(blob);
            link.download   = filename;
            link.click();
            URL.revokeObjectURL(link.href);
            trackDownload("Agent Event Log CSV", { agent, days });

        } catch (err) {
            console.error("Event log export failed:", err);
            alert(`Export failed: ${err.message}`);
        } finally {
            setExportLoading(false);
        }
    };

    // ── Fetch report data once — passed as props to child components ────────
    useEffect(() => {
        const fetchAgentThreats = async () => {
            setReportLoading(true);
            try {
                const authUser = JSON.parse(sessionStorage.getItem("authUser"));
                const headers  = {
                    "Content-Type": "application/json",
                    ...(authUser?.token && { Authorization: `Bearer ${authUser.token}` }),
                };
                const url = agent?.toLowerCase() === "all"
                    ? `${process.env.REACT_APP_API_BASE_URL}/threats/report/all`
                    : `${process.env.REACT_APP_API_BASE_URL}/threats/report/${agent}`;
                const response = await fetch(url, { headers });
                const data     = await response.json();
                if (data.success) setReportData(data);
            } catch (err) {
                console.error("Error fetching agent report:", err);
            } finally {
                setReportLoading(false);
            }
        };
        fetchAgentThreats();
    }, [agent, days]);

    return (
        <div
            className="page-content px-4 pt-4 mt-5 mb-4"
            style={{ backgroundColor: "#ffffff", color: "#000000", padding: "20px" }}
        >
            <Container fluid>

                {/* ── Toolbar ── */}
                <Row className="mt-1 mb-3 align-items-center">
                    <Col>
                        <Button
                            onClick={() => navigate(-1)}
                            style={{
                                backgroundColor: "transparent",
                                border: "1px solid rgba(0,0,0,0.15)",
                                color: "#555",
                                borderRadius: "8px",
                                fontSize: "13px",
                                fontWeight: 500,
                                padding: "7px 14px",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px",
                            }}
                        >
                            <RiArrowLeftLine size={15} />
                            Back to Reports
                        </Button>
                    </Col>

                    <Col className="text-end d-flex justify-content-end align-items-center gap-2">

                        {/* Event log export */}
                        <Button
                            onClick={downloadEventLog}
                            disabled={exportLoading}
                            style={{
                                backgroundColor: "transparent",
                                border: `1px solid ${ORANGE_COLORS.primary}`,
                                color: ORANGE_COLORS.primary,
                                borderRadius: "8px",
                                fontSize: "13px",
                                fontWeight: 500,
                                padding: "7px 14px",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px",
                                transition: "all 0.15s ease",
                            }}
                            onMouseEnter={e => { e.currentTarget.style.backgroundColor = `${ORANGE_COLORS.primary}12`; }}
                            onMouseLeave={e => { e.currentTarget.style.backgroundColor = "transparent"; }}
                        >
                            {exportLoading
                                ? <><Spinner size="sm" /> Exporting…</>
                                : <><RiFileDownloadLine size={15} /> Export Event Log</>
                            }
                        </Button>

                        {/* PDF download */}
                        <Button
                            onClick={downloadPDF}
                            disabled={pdfLoading}
                            style={{
                                backgroundColor: ORANGE_COLORS.primary,
                                border: "none",
                                color: "#fff",
                                borderRadius: "8px",
                                fontSize: "13px",
                                fontWeight: 500,
                                padding: "7px 14px",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px",
                            }}
                        >
                            {pdfLoading
                                ? <><Spinner size="sm" /> Generating…</>
                                : <><RiFileChartLine size={15} /> Download PDF</>
                            }
                        </Button>
                    </Col>
                </Row>

                {/* ── Printable report area ── */}
                <div id="AgentReport" style={{ backgroundColor: "#ffffff", color: "#000000", padding: "24px" }}>

                    {/* Report header */}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28, paddingBottom: 20, borderBottom: "2px solid #FF5B00" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                            <img src={img} alt="Logo" style={{ width: 64, height: "auto" }} />
                            <div>
                                <h2 style={{ fontSize: 20, fontWeight: 800, color: "#1f2937", margin: 0, lineHeight: 1.2 }}>
                                    Threat Analysis Report
                                </h2>
                                <p style={{ fontSize: 12, color: "#6b7280", margin: "4px 0 0" }}>
                                    Agent: <strong style={{ color: "#FF5B00" }}>{agent}</strong>
                                    {days && days !== "other" ? ` · Last ${days} days` : " · All time"}
                                </p>
                            </div>
                        </div>
                        <div style={{ textAlign: "right", fontSize: 11, color: "#9ca3af" }}>
                            <div>Generated on</div>
                            <div style={{ fontWeight: 600, color: "#6b7280" }}>{new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}</div>
                        </div>
                    </div>

                    {/* Stat tiles */}
                    {!reportLoading && reportData?.summary && (() => {
                        const s = reportData.summary;
                        const tiles = [
                            { label: "Total Threats", value: s.total_count || 0, color: "#FF5B00", bg: "#FFF4EF" },
                            { label: "Critical",      value: s.critical_count || 0, color: "#dc2626", bg: "#fef2f2" },
                            { label: "High",          value: s.high_count || 0,     color: "#ea580c", bg: "#fff7ed" },
                            { label: "Medium",        value: s.medium_count || 0,   color: "#d97706", bg: "#fffbeb" },
                            { label: "Low",           value: s.low_count || 0,      color: "#16a34a", bg: "#f0fdf4" },
                        ];
                        return (
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 12, marginBottom: 28 }}>
                                {tiles.map(t => (
                                    <div key={t.label} style={{ background: t.bg, border: `1px solid ${t.color}22`, borderRadius: 10, padding: "14px 16px", textAlign: "center" }}>
                                        <div style={{ fontSize: 26, fontWeight: 800, color: t.color, lineHeight: 1 }}>{t.value.toLocaleString()}</div>
                                        <div style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", marginTop: 4, textTransform: "uppercase", letterSpacing: "0.04em" }}>{t.label}</div>
                                    </div>
                                ))}
                            </div>
                        );
                    })()}

                    {/* Executive Summary */}
                    <div style={{ marginBottom: 28 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                            <div style={{ width: 4, height: 20, background: "#FF5B00", borderRadius: 2, flexShrink: 0 }} />
                            <h6 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#1f2937", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                                Executive Summary
                            </h6>
                        </div>
                        <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 10, padding: "16px 20px", fontSize: 13, color: "#374151", lineHeight: 1.7 }}>
                            The Threat Analysis Report summarizes active and resolved threats across production assets,
                            including indicators of compromise and detection evidence. Key findings are mapped to
                            compliance frameworks (PCI DSS, GDPR, ISO 27001) and MITRE ATT&CK techniques, ensuring
                            alignment with governance requirements and providing actionable recommendations for risk mitigation.
                        </div>
                    </div>

                    <Charts reportData={reportData} loading={reportLoading} />
                    <AssetOverview reportData={reportData} loading={reportLoading} />
                    <ThreatList />
                    <RecommendationSumary reportData={reportData} loading={reportLoading} />
                </div>

            </Container>
        </div>
    );
}

export default AgentReport;